import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Paper,
  Divider,
  Alert,
  Grid,
} from '@mui/material';
import { Print, QrCode } from '@mui/icons-material';

type LabelSize = 'small' | 'medium' | 'large';

interface LabelField {
  key: string;
  label: string;
  enabled: boolean;
}

const LABEL_SIZES: { value: LabelSize; label: string; width: number; height: number }[] = [
  { value: 'small', label: 'Small (25mm x 15mm)', width: 120, height: 72 },
  { value: 'medium', label: 'Medium (50mm x 30mm)', width: 200, height: 120 },
  { value: 'large', label: 'Large (100mm x 60mm)', width: 300, height: 180 },
];

const DEFAULT_FIELDS: LabelField[] = [
  { key: 'code', label: 'Entity Code', enabled: true },
  { key: 'name', label: 'Entity Name', enabled: true },
  { key: 'type', label: 'Entity Type', enabled: true },
  { key: 'date', label: 'Print Date', enabled: false },
  { key: 'metadata1', label: 'Additional Info 1', enabled: false },
  { key: 'metadata2', label: 'Additional Info 2', enabled: false },
  { key: 'company', label: 'Company Name', enabled: false },
];

const LabelDesigner: React.FC = () => {
  const [labelSize, setLabelSize] = useState<LabelSize>('medium');
  const [fields, setFields] = useState<LabelField[]>(DEFAULT_FIELDS);
  const [printMessage, setPrintMessage] = useState('');

  const sizeConfig = LABEL_SIZES.find((s) => s.value === labelSize)!;
  const enabledFields = fields.filter((f) => f.enabled);

  const toggleField = (key: string) => {
    setFields((prev) =>
      prev.map((f) => (f.key === key ? { ...f, enabled: !f.enabled } : f))
    );
  };

  const handlePrint = () => {
    const printContent = document.getElementById('label-preview-print');
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>QR Label Print</title>
          <style>
            body { margin: 0; padding: 16px; font-family: Arial, sans-serif; }
            .label { border: 1px solid #000; padding: 8px; display: inline-flex; align-items: center; gap: 8px; }
            .label-text { font-size: 10px; }
            .label-code { font-weight: bold; font-size: 12px; }
            .label-type { font-size: 9px; color: #555; text-transform: uppercase; }
          </style>
        </head>
        <body>
          <div class="label">
            <div class="qr-placeholder" style="width:60px;height:60px;border:1px dashed #999;display:flex;align-items:center;justify-content:center;font-size:8px;color:#999;">QR</div>
            <div class="label-text">
              ${enabledFields.map((f) => `<div class="label-${f.key}">[${f.label}]</div>`).join('')}
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
    setPrintMessage('Print dialog opened');
    setTimeout(() => setPrintMessage(''), 3000);
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Controls */}
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Label Settings
              </Typography>

              <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                <InputLabel>Label Size</InputLabel>
                <Select
                  value={labelSize}
                  label="Label Size"
                  onChange={(e) => setLabelSize(e.target.value as LabelSize)}
                >
                  {LABEL_SIZES.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography variant="subtitle2" gutterBottom>
                Fields to Include
              </Typography>
              <FormGroup>
                {fields.map((field) => (
                  <FormControlLabel
                    key={field.key}
                    control={
                      <Checkbox
                        checked={field.enabled}
                        onChange={() => toggleField(field.key)}
                        size="small"
                      />
                    }
                    label={field.label}
                  />
                ))}
              </FormGroup>

              <Divider sx={{ my: 2 }} />

              {printMessage && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {printMessage}
                </Alert>
              )}

              <Button
                variant="contained"
                startIcon={<Print />}
                fullWidth
                onClick={handlePrint}
                sx={{ py: 1.5 }}
              >
                Print Label
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Preview */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Label Preview
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" mb={2}>
                {sizeConfig.label} — Preview below (not to scale)
              </Typography>

              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                py={4}
                bgcolor="grey.100"
                borderRadius={2}
              >
                <Paper
                  id="label-preview-print"
                  elevation={3}
                  sx={{
                    width: sizeConfig.width,
                    height: sizeConfig.height,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    border: '1px solid #000',
                    overflow: 'hidden',
                  }}
                >
                  {/* QR Placeholder */}
                  <Box
                    sx={{
                      width: sizeConfig.height * 0.7,
                      height: sizeConfig.height * 0.7,
                      minWidth: sizeConfig.height * 0.7,
                      border: '1px dashed grey',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.50',
                      flexShrink: 0,
                    }}
                  >
                    <QrCode sx={{ fontSize: sizeConfig.height * 0.5, color: 'grey.400' }} />
                  </Box>

                  {/* Text Fields */}
                  <Box flex={1} overflow="hidden">
                    {enabledFields.map((field) => (
                      <Typography
                        key={field.key}
                        variant={field.key === 'code' ? 'body2' : 'caption'}
                        display="block"
                        fontWeight={field.key === 'code' ? 700 : 400}
                        color={field.key === 'type' ? 'textSecondary' : 'textPrimary'}
                        noWrap
                        sx={{ fontSize: labelSize === 'small' ? '0.55rem' : undefined }}
                      >
                        [{field.label}]
                      </Typography>
                    ))}
                    {enabledFields.length === 0 && (
                      <Typography variant="caption" color="textSecondary">
                        No fields selected
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                Label previews show placeholder text. Actual labels will display real entity data when printed from the Batch Print tab.
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LabelDesigner;
